/**
 * MACDChart - MACD histogram with signal line
 */

import { useEffect, useRef, useState } from 'react';
import { createChart } from 'lightweight-charts';
import './MACDChart.css';

export default function MACDChart({ data, height = 150, onVisibleRangeChange }) {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const histogramSeriesRef = useRef(null);
  const [macdValues, setMacdValues] = useState(null);

  useEffect(() => {
    if (!chartContainerRef.current || !data || !data.macd) return;

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height,
      layout: {
        background: { color: '#1a1a1a' },
        textColor: '#d1d4dc',
      },
      grid: {
        vertLines: { color: '#2b2b2b' },
        horzLines: { color: '#2b2b2b' },
      },
      crosshair: {
        mode: 1,
      },
      timeScale: {
        borderColor: '#2b2b2b',
        visible: false, // Hide time scale for indicator charts
      },
      leftPriceScale: {
        borderColor: '#2b2b2b',
        autoScale: true,
        visible: true,
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
      },
      rightPriceScale: {
        visible: false,
      },
    });

    chartRef.current = chart;

    // Add MACD histogram
    if (data.macd.histogram && data.macd.histogram.length > 0) {
      const histogramSeries = chart.addHistogramSeries({
        priceScaleId: 'left',
        color: '#26a69a',
        priceFormat: {
          type: 'volume',
        },
      });
      histogramSeriesRef.current = histogramSeries;

      const histogramData = data.macd.histogram.map(d => ({
        time: d.time,
        value: d.value,
        color: d.value >= 0 ? '#26a69a' : '#ef5350',
      }));

      histogramSeries.setData(histogramData);
    }

    // Add MACD line
    if (data.macd.macd && data.macd.macd.length > 0) {
      const macdSeries = chart.addLineSeries({
        priceScaleId: 'left',
        color: '#2196F3',
        lineWidth: 2,
      });

      const macdData = data.macd.macd.map(d => ({
        time: d.time,
        value: d.value,
      }));

      macdSeries.setData(macdData);
    }

    // Add Signal line
    if (data.macd.signal && data.macd.signal.length > 0) {
      const signalSeries = chart.addLineSeries({
        priceScaleId: 'left',
        color: '#ff9800',
        lineWidth: 2,
      });

      const signalData = data.macd.signal.map(d => ({
        time: d.time,
        value: d.value,
      }));

      signalSeries.setData(signalData);
    }

    // Subscribe to crosshair movement for value display
    chart.subscribeCrosshairMove((param) => {
      if (param.time && param.seriesData && param.seriesData.size > 0) {
        let macdVal = null, signalVal = null, histVal = null;

        param.seriesData.forEach((data, series) => {
          if (data && typeof data.value !== 'undefined') {
            // Identify series by checking data structure or series reference
            if (data.color) {
              histVal = data.value; // Histogram has color property
            } else {
              // Check line color to distinguish MACD from Signal
              const seriesOptions = series.options();
              if (seriesOptions.color === '#2196F3') {
                macdVal = data.value;
              } else if (seriesOptions.color === '#ff9800') {
                signalVal = data.value;
              }
            }
          }
        });

        setMacdValues({ macd: macdVal, signal: signalVal, histogram: histVal });
      } else if (data.macd.macd && data.macd.macd.length > 0) {
        // Show latest values when no crosshair
        const latest = data.macd.macd.length - 1;
        setMacdValues({
          macd: data.macd.macd[latest]?.value,
          signal: data.macd.signal?.[latest]?.value,
          histogram: data.macd.histogram?.[latest]?.value
        });
      }

      // Sync crosshair across all charts
      if (window.chartSync && chart) {
        window.chartSync.syncCrosshair(chart, param);
      }
    });

    // Initialize with latest values
    if (data.macd.macd && data.macd.macd.length > 0) {
      const latest = data.macd.macd.length - 1;
      setMacdValues({
        macd: data.macd.macd[latest]?.value,
        signal: data.macd.signal?.[latest]?.value,
        histogram: data.macd.histogram?.[latest]?.value
      });
    }

    if (onVisibleRangeChange) {
      chart.timeScale().subscribeVisibleTimeRangeChange(onVisibleRangeChange);
    }

    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [data, height, onVisibleRangeChange]);

  useEffect(() => {
    if (chartRef.current && histogramSeriesRef.current && window.chartSync) {
      window.chartSync.registerChart('macd', chartRef.current, histogramSeriesRef.current);
    }
  }, [chartRef.current, histogramSeriesRef.current]);

  return (
    <div className="macd-chart">
      <div className="chart-value-display">
        {macdValues && (
          <span className="macd-values">
            MACD: {macdValues.macd ? parseFloat(macdValues.macd).toFixed(4) : null ?? 'N/A'} Signal: {macdValues.signal ? parseFloat(macdValues.signal).toFixed(4) : null ?? 'N/A'} Hist: {macdValues.histogram ? parseFloat(macdValues.histogram).toFixed(4) : null ?? 'N/A'}
          </span>
        )}
      </div>
      <div ref={chartContainerRef} className="chart-container" />
    </div>
  );
}
